import React from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Palette,
  Check,
  DarkMode,
  LightMode,
  Park,
  AutoAwesome,
  Whatshot,
} from '@mui/icons-material';
import { useAppTheme } from '../../theme';

const themeIcons: Record<string, React.ReactElement> = {
  blue: <LightMode fontSize="small" />,
  dark: <DarkMode fontSize="small" />,
  green: <Park fontSize="small" />,
  purple: <AutoAwesome fontSize="small" />,
  orange: <Whatshot fontSize="small" />,
};

const ThemeSwitcher: React.FC = () => {
  const { currentTheme, setTheme, availableThemes } = useAppTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeSelect = (themeId: string) => {
    setTheme(themeId);
    handleClose();
  };

  return (
    <>
      <Tooltip title="Change Theme">
        <IconButton
          onClick={handleClick}
          color="inherit"
          sx={{
            // Was a hardcoded white wash, which is invisible now the top bar is
            // a light surface. `action.hover` is derived from the active
            // palette mode, so it stays visible on the light presets and on the
            // dark one without this component knowing which is in play.
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Palette />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 2,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            SELECT THEME
          </Typography>
        </Box>

        {availableThemes.map((theme) => (
          <MenuItem
            key={theme.id}
            onClick={() => handleThemeSelect(theme.id)}
            selected={currentTheme.id === theme.id}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
              },
            }}
          >
            <ListItemIcon>
              {themeIcons[theme.id] || <Palette fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={theme.name} />
            {currentTheme.id === theme.id && (
              <Check fontSize="small" color="primary" />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ThemeSwitcher;
